import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnInit,
  ViewContainerRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonsModule, KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import {
  SelectEvent as TabSelectEvent,
  TabStripModule,
} from '@progress/kendo-angular-layout';
import { InputsModule, SwitchModule } from '@progress/kendo-angular-inputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  catchError,
  finalize,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  OUTLOOK_SYNC_CALENDAR_ROLLING_WINDOW_FEATURE_FLAG,
  OUTLOOK_SYNC_FREQUENCY,
  OutlookSyncOAuthFlowService,
  OutlookSyncConfigurationPayload,
  OutlookSyncStatus,
  OutlookSyncStore,
  OutlookSyncTab,
  OutlookSyncTokenType,
  OutlookSyncType,
  SyncConfigurationFacade,
  SyncHealthStatus,
  SyncHistory,
  UpdateSyncConfiguration,
  UserSyncConfiguration,
  computePatExpiration,
} from '@maximizer/outlook-sync/data-access';
import { PersonalAccessTokenService } from '@maximizer/core/personal-access-token/data-access';
import { OutlookSyncFormControls, OutlookSyncFormGroup } from '../form.model';
import { OutlookEmailTabComponent } from './tabs/email-tab/outlook-email-tab.component';
import { OutlookContactTabComponent } from './tabs/contact-tab/outlook-contact-tab.component';
import { OutlookCalendarTabComponent } from './tabs/calendar-tab/outlook-calendar-tab.component';
import { OutlookTaskTabComponent } from './tabs/task-tab/outlook-task-tab.component';
import {
  ContextService,
  FeatureService,
  SessionService,
  TrackEventService,
} from '@maximizer/core/shared/data-access';
import { CDN_URL, UserRights } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  NotificationComponent,
  UI_NOTIFICATION,
} from '@maximizer/core/shared/ui/components/notification';
import { forkJoin, interval, merge, Observable, of } from 'rxjs';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { ActionEventType } from '@maximizer/core/shared/ui/config';
import { normalizeError } from '@maximizer/shared/util';
import { HISTORY_PAGE_SIZE } from './utils/history-mapper.util';

const TRACKER_MODULE = 'sync-configuration';

function isUserSyncConfiguration(value: unknown): value is UserSyncConfiguration {
  return (
    !!value &&
    typeof value === 'object' &&
    'id' in value &&
    'emailsSync' in value
  );
}

const HISTORY_POLL_INTERVAL_MS = 60_000;

const POLLED_SYNC_TYPES = [
  OutlookSyncType.Contacts,
  OutlookSyncType.Appointments,
  OutlookSyncType.Tasks,
  OutlookSyncType.Emails,
] as const;

// computeSaveDelta() keys -> sync type (only the 4 toggle keys; others ignored).
const SYNC_DELTA_KEY_TO_TYPE: Readonly<Record<string, OutlookSyncType>> = {
  contactsSync: OutlookSyncType.Contacts,
  emailsSync: OutlookSyncType.Emails,
  appointmentsSync: OutlookSyncType.Appointments,
  tasksSync: OutlookSyncType.Tasks,
};

interface LatestHistory {
  status: OutlookSyncStatus;
  at: number;
}

// Single source for "latest" ordering; avoids duplicated Date.parse calls.
const historyTime = (entry: SyncHistory): number =>
  Date.parse(entry.startedAt ?? entry.completedAt ?? '') || 0;

// Controls disabled together when a tab is blocked by Maximizer permissions.
const TAB_CONTROLS: Record<OutlookSyncTab, (keyof OutlookSyncFormControls)[]> = {
  contacts: [
    'contactsSyncEnabled',
    'favoriteListIds',
    'favoriteListName',
    'outlookCategoryIds',
  ],
  appointments: [
    'appointmentsSyncEnabled',
    'appointmentsRollingWindowDaysBack',
    'appointmentsRollingWindowDaysAfter',
  ],
  tasks: ['tasksSyncEnabled'],
  emails: ['emailsSyncEnabled', 'emailsSyncSyncEverything'],
};

interface TabDescriptor {
  tab: OutlookSyncTab;
  syncType: OutlookSyncType;
  labelKey: string;
  status: 'active' | 'paused';
}

@Component({
  selector: 'maximizer-sync-configuration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonsModule,
    DialogModule,
    InputsModule,
    SwitchModule,
    TabStripModule,
    TooltipModule,
    TranslatePipe,
    OutlookEmailTabComponent,
    OutlookContactTabComponent,
    OutlookCalendarTabComponent,
    OutlookTaskTabComponent,
    KENDO_BUTTON,
    UI_LOADER,
    UI_NOTIFICATION,
  ],
  templateUrl: './sync-configuration.component.html',
  styleUrl: './sync-configuration.component.scss',
  host: { class: 'flex flex-col h-full w-full min-h-0 min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncConfigurationComponent
  extends EmbeddedComponent
  implements OnInit
{
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly outlookSync = inject(SyncConfigurationFacade);
  private readonly context = inject(ContextService);
  private readonly feature = inject(FeatureService);
  private readonly session = inject(SessionService);
  private readonly _ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly oauthFlow = inject(OutlookSyncOAuthFlowService);
  private readonly patService = inject(PersonalAccessTokenService);
  private readonly trackEvent = inject(TrackEventService);
  readonly store = inject(OutlookSyncStore);
  protected readonly cdn = inject(CDN_URL);

  private userInitiatedReauth = false;

  protected notificationContainerRef = viewChild('notificationContainer', {
    read: ViewContainerRef,
  });
  protected successToast = viewChild<NotificationComponent>('successToast');
  protected errorToast = viewChild<NotificationComponent>('errorToast');
  protected readonly successToastMessage = signal<string>('');
  protected readonly errorToastMessage = signal<string>('');

  private readonly _controlStateEffect = effect(() => {
    const locked = this.store.saving() || this.store.configLoadFailed();
    const access = this.syncAccess();
    if (locked) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
    for (const tab of Object.keys(access) as OutlookSyncTab[]) {
      if (access[tab]) {
        continue;
      }
      for (const name of TAB_CONTROLS[tab]) {
        this.form.controls[name].disable({ emitEvent: false });
      }
    }
  });

  private readonly _resultEffect = effect(() => {
    const result = this.store.lastResult();
    if (!result) return;
    if (result.type === 'success') {
      this.successToastMessage.set(result.message);
      queueMicrotask(() => this.successToast()?.show());
    } else {
      this.errorToastMessage.set(result.message);
      queueMicrotask(() => this.errorToast()?.show());
    }
  });

  protected readonly reauthenticating = signal(false);
  // Suppresses the connection warning while returning from a Microsoft OAuth
  // callback until the fresh health-status settles, so the stale unhealthy
  // state doesn't flicker the Re-authenticate banner back in.
  private readonly resumingReauth = signal(false);
  protected readonly form: OutlookSyncFormGroup = this.fb.group({
    contactsSyncEnabled: this.fb.control(false),
    appointmentsSyncEnabled: this.fb.control(false),
    tasksSyncEnabled: this.fb.control(false),
    emailsSyncEnabled: this.fb.control(false),
    emailsSyncSyncEverything: this.fb.control(true),
    appointmentsRollingWindowDaysBack: this.fb.control(180, [
      Validators.min(0),
      Validators.max(730),
    ]),
    appointmentsRollingWindowDaysAfter: this.fb.control(30, [
      Validators.min(0),
      Validators.max(365),
    ]),
    favoriteListIds: this.fb.control<string[]>([]),
    favoriteListName: this.fb.control<string | null>(null),
    outlookCategoryIds: this.fb.control<string[]>([]),
  });

  private historyPollingStarted = false;
  private readonly reenabledTypes = signal<ReadonlySet<OutlookSyncType>>(
    new Set(),
  );
  protected readonly activeTab = signal<OutlookSyncTab>(
    this.store.selectedTab() ?? 'emails',
  );
  protected readonly payload = signal<OutlookSyncConfigurationPayload | null>(
    null,
  );
  protected readonly userSyncConfiguration =
    signal<UserSyncConfiguration | null>(null);
  protected readonly sessionEmail = signal<string>('');
  protected readonly recentlyAuthRecovered = signal(false);
  protected readonly healthStatus = signal<SyncHealthStatus | null>(null);
  private readonly userRights = signal<UserRights | null>(null);

  // Per-tab sync access derived from Maximizer permissions. Defaults to
  // all-allowed until rights load to avoid a disabled -> enabled flash.
  protected readonly syncAccess = computed<Record<OutlookSyncTab, boolean>>(
    () => {
      const rights = this.userRights();
      if (!rights) {
        return { contacts: true, appointments: true, tasks: true, emails: true };
      }
      const ab = rights.addressBook;
      return {
        contacts: ab.read && ab.insert && ab.update && rights.syncContacts,
        appointments: ab.read,
        tasks: ab.read,
        emails: ab.read,
      };
    },
  );
  protected get showPermissionWarning(): boolean {
    if (this.userRights() === null) {
      return false;
    }
    const access = this.syncAccess();
    return this.tabs.some((tab) => !access[tab.tab]);
  }
  protected readonly latestHistoryByType = computed<
    Partial<Record<OutlookSyncType, LatestHistory>>
  >(() => {
    const byType = this.store.lastHistoryByType();
    const out: Partial<Record<OutlookSyncType, LatestHistory>> = {};
    for (const type of POLLED_SYNC_TYPES) {
      const entries = byType[type];
      if (!entries?.length) continue;
      let latest: SyncHistory | undefined;
      for (const entry of entries) {
        if (!latest || historyTime(entry) > historyTime(latest)) {
          latest = entry;
        }
      }
      if (latest) {
        out[type] = { status: latest.syncStatus, at: historyTime(latest) };
      }
    }
    return out;
  });

  protected getTabIconKind(
    tab: TabDescriptor,
  ): 'success' | 'syncing' | 'error' | 'waiting' | 'paused' {
    if (tab.status !== 'active') {
      return 'paused';
    }
    const last = this.latestHistoryByType()[tab.syncType];
    const status = last?.status;
    if (
      status === OutlookSyncStatus.Syncing ||
      status === OutlookSyncStatus.Enqueued
    ) {
      return 'syncing';
    }
    if (
      status === OutlookSyncStatus.Problem ||
      status === OutlookSyncStatus.Warning
    ) {
      // Stale problem: type re-enabled after the failed run, no newer sync yet -> green.
      const updatedAt =
        Date.parse(this.userSyncConfiguration()?.updatedAt ?? '') || 0;
      if (
        this.reenabledTypes().has(tab.syncType) &&
        updatedAt > (last?.at ?? 0)
      ) {
        return 'success';
      }
      return this.recentlyAuthRecovered() ? 'waiting' : 'error';
    }
    return 'success';
  }

  protected readonly tabs: TabDescriptor[] = [
    {
      tab: 'emails',
      syncType: OutlookSyncType.Emails,
      labelKey: 'outlookSync.tabs.email.title',
      status: 'paused',
    },
    {
      tab: 'contacts',
      syncType: OutlookSyncType.Contacts,
      labelKey: 'outlookSync.tabs.contact.title',
      status: 'paused',
    },
    {
      tab: 'appointments',
      syncType: OutlookSyncType.Appointments,
      labelKey: 'outlookSync.tabs.calendar.title',
      status: 'paused',
    },
    {
      tab: 'tasks',
      syncType: OutlookSyncType.Tasks,
      labelKey: 'outlookSync.tabs.task.title',
      status: 'paused',
    },
  ];

  protected readonly loading = signal(true);
  protected readonly selectedTabIndex = signal(0);

  constructor() {
    super(inject<ElementRef<HTMLElement>>(ElementRef));
    this.registerTabStatusListeners();
  }

  override doAction(name: string, args: unknown): void {
    this._ngZone.run(() => {
      if (name === ActionEventType.Show) {
        const prefetched = isUserSyncConfiguration(args) ? args : null;
        this.show(prefetched);
      } else {
        super.doAction(name, args);
      }
    });
  }

  ngOnInit(): void {
    this.ensureActiveTabInList();

    this.session
      .getInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          this.sessionEmail.set(session?.user?.email1?.email ?? '');
          this.userRights.set(session?.user?.rights ?? null);
        },
        error: (error) => {
          console.error('Failed to load session for permission check', error);
        },
      });

    const params = new URLSearchParams(globalThis.location.search);
    this.userInitiatedReauth = params.get('reauth') === '1';
    const callbackState = params.get('state');
    const hasCallback = ['code', 'error', 'error_description'].some((param) =>
      params.has(param),
    );
    if (
      hasCallback &&
      callbackState &&
      !callbackState.startsWith('install-callback:')
    ) {
      this.resumingReauth.set(true);
      this.oauthFlow.resumeFromUrl().then(
        () => {
          this.clearOauthParamsFromUrl();
          this.handleOauthResumeResult();
        },
        (error) => {
          this.clearOauthParamsFromUrl();
          console.error('Non-install OAuth resume failed', error);
          this.store.reportError(
            this.translate.instant('outlookSync.messages.reauthError'),
          );
          this.resumingReauth.set(false);
          this.loading.set(false);
        },
      );
    }
  }

  private clearOauthParamsFromUrl(): void {
    const url = new URL(globalThis.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    globalThis.history.replaceState({}, '', url.toString());
  }

  private captureHealth(health: SyncHealthStatus | undefined): void {
    if (health) {
      this.healthStatus.set(health);
    }
  }

  private handleOauthResumeResult(): void {
    if (this.oauthFlow.stage() === 'error') {
      if (this.oauthFlow.errorCode() !== 'access_denied') {
        this.store.reportError(
          this.translate.instant('outlookSync.messages.reauthError'),
        );
      }
      this.resumingReauth.set(false);
      this.loading.set(false);
      return;
    }

    const configId = this.oauthFlow.completedConfigurationId();
    if (!configId) {
      this.resumingReauth.set(false);
      this.loading.set(false);
      return;
    }

    this.outlookSync
      .getHealthStatus(configId)
      .pipe(
        catchError((error) => {
          console.error('Health check after OAuth resume failed', error);
          return of(undefined);
        }),
        finalize(() => {
          this.resumingReauth.set(false);
          this.loading.set(false);
        }),
      )
      .subscribe((health) => {
        this.captureHealth(health);
        // Gate success on a confirmed-healthy backend; undefined/unhealthy means re-auth didn't work.
        if (!health?.isMicrosoftAuthHealthy) {
          this.store.reportError(
            this.translate.instant(
              'outlookSync.messages.saveError',
            ),
          );
          return;
        }
        const current = this.payload();
        if (current) {
          this.payload.set({ ...current, needsManualIntervention: false });
        }
        this.recentlyAuthRecovered.set(true);
        this.store.reportSuccess(
          this.translate.instant(
            'outlookSync.messages.updateSuccess',
          ),
        );
      });
  }

  private ensureActiveTabInList(): void {
    const current = this.activeTab();
    const currentIndex = this.tabs.findIndex((tab) => tab.tab === current);
    if (currentIndex >= 0) {
      this.selectedTabIndex.set(currentIndex);
      this.store.ensureSelectedTab(current);
      return;
    }

    const fallbackTab = this.tabs[0].tab;
    this.activeTab.set(fallbackTab);
    this.selectedTabIndex.set(0);
    this.store.ensureSelectedTab(fallbackTab);
  }

  private show(prefetched: UserSyncConfiguration | null = null) {
    if (prefetched) {
      this.applyConfiguration(prefetched);
      this.loading.set(false);
      return;
    }

    this.resolveConfigurationFromSession();
  }

  private resolveConfigurationFromSession(): void {
    this.session
      .getInfo()
      .pipe(
        switchMap((session) => {
          const userId = session?.user?.id ?? '';
          const workspaceId = session?.workspace ?? '';
          if (!userId || !workspaceId) {
            console.warn(
              'Session missing user.id/workspace; cannot fetch sync configuration.',
            );
            return of(undefined);
          }
          return this.outlookSync.getConfiguration(userId, workspaceId);
        }),
        tap((data) => {
          if (!data) {
            console.warn(
              'OutlookSync API returned no configuration for this user/workspace (204 No Content). A configuration must exist before the UI can render.',
            );
            this.store.reportConfigLoadFailure();
            return;
          }
          this.applyConfiguration(data);
        }),
        catchError((error) => {
          console.warn('Failed to resolve sync configuration', error);
          if (
            error instanceof HttpErrorResponse &&
            error.status === HttpStatusCode.Unauthorized
          ) {
            this.store.reportAuthFailure();
          } else {
            this.store.reportConfigLoadFailure();
          }
          return of(undefined);
        }),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe();
  }

  private applyConfiguration(data: UserSyncConfiguration): void {
    this.store.reportConfigLoadSuccess();
    this.userSyncConfiguration.set(data);
    const payload: OutlookSyncConfigurationPayload = {
      userSyncConfigurationId: data.id,
      emailsSyncEnabled: data.emailsSync.enabled ?? false,
      emailsSyncSyncEverything: data.emailsSync.syncEverything ?? true,
      appointmentsSyncEnabled: data.appointmentsSync.enabled ?? false,
      appointmentsRollingWindowDaysBack:
        data.appointmentsSync.rollingWindowDaysBack,
      appointmentsRollingWindowDaysAfter:
        this.getCalendarRollingWindowDaysAfter(
          data.appointmentsSync.rollingWindowDaysAfter,
        ),
      tasksSyncEnabled: data.tasksSync.enabled ?? false,
      contactsSyncEnabled: data.contactsSync.enabled ?? false,
      userId: data.maximizerUserUid ?? '',
      alias: data.tenantAlias ?? '',
      workspaceId: data.workspaceId ? data.workspaceId.toString() : '',
      needsManualIntervention: data.needsManualIntervention ?? false,
    };

    this.applyPayload(payload);
    this.setSyncStatus(payload);
    // During an OAuth resume the callback path owns the authoritative health
    // check; skip auto-heal so its stale health-status can't clobber the fresh one.
    if (!this.resumingReauth()) {
      this.autoHealMaximizerAuth()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }

    this.startHistoryPolling();
  }

  private autoHealMaximizerAuth(): Observable<void> {
    if (this.store.autoHealAttempted()) {
      return of(void 0);
    }
    const configurationId = this.getConfigurationId();
    const maximizerUserUid =
      this.userSyncConfiguration()?.maximizerUserUid ??
      this.payload()?.userId ??
      '';
    if (!configurationId || !maximizerUserUid) {
      return of(void 0);
    }
    this.store.markAutoHealAttempted();

    return this.outlookSync.getHealthStatus(configurationId).pipe(
      catchError((error) => {
        console.warn('Auto-heal: health-status fetch failed', error);
        return of(undefined);
      }),
      switchMap((health) => {
        this.captureHealth(health);
        if (health?.isMicrosoftAuthHealthy) {
          const current = this.payload();
          if (current?.needsManualIntervention) {
            this.payload.set({ ...current, needsManualIntervention: false });
          }
        }
        if (!health || health.isMaximizerAuthHealthy) {
          return of(void 0);
        }
        return this.refreshMaximizerAuth(configurationId, maximizerUserUid);
      }),
    );
  }

  private refreshMaximizerAuth(
    configurationId: string,
    maximizerUserUid: string,
  ): Observable<void> {
    const patExpiration = computePatExpiration();
    return this.patService
      .create('outlook-sync', patExpiration.isoDate, maximizerUserUid)
      .pipe(
        catchError((error) => {
          console.error('Auto-heal: PAT creation failed', error);
          return of(null);
        }),
        switchMap((patKey) => {
          if (!patKey) {
            this.reportReauthFailure();
            return of(void 0);
          }
          const settings: UpdateSyncConfiguration = {
            maximizerUserUid,
            maximizerAuth: {
              tokenType: OutlookSyncTokenType.PersonalAccessToken,
              personalAccessToken: patKey,
              expiresIn: patExpiration.expiresInSeconds.toString(),
            },
          };
          return this.outlookSync
            .updateConfiguration(settings, configurationId)
            .pipe(
              catchError(() => of(false)),
              switchMap((ok) => {
                if (!ok) {
                  this.reportReauthFailure();
                  return of(void 0);
                }
                return this.confirmMaximizerAuthHealed(configurationId);
              }),
            );
        }),
      );
  }

  private confirmMaximizerAuthHealed(
    configurationId: string,
  ): Observable<void> {
    return this.outlookSync.getHealthStatus(configurationId).pipe(
      catchError(() => of(undefined)),
      map((health) => {
        this.captureHealth(health);
        if (!health?.isMaximizerAuthHealthy) {
          this.reportReauthFailure();
          return;
        }
        this.recentlyAuthRecovered.set(true);
        this.reloadConfiguration(configurationId);
        if (this.userInitiatedReauth) {
          this.store.reportReauthSuccess();
        }
      }),
    );
  }

  private reportReauthFailure(): void {
    this.store.reportError(
      this.translate.instant('outlookSync.messages.reauthError'),
    );
    this.store.resetAutoHealAttempted();
  }

  protected onTabSelect(event: TabSelectEvent): void {
    const target = this.tabs[event.index]?.tab;
    if (!target) {
      return;
    }
    this.selectTab(target);
    this.selectedTabIndex.set(event.index);
    this.trackSyncConfigurationEvent('tab', 'clicked', { tab: target });
  }

  protected saveConfiguration(): void {
    const configurationId = this.getConfigurationId();
    const maximizerUserUid =
      this.userSyncConfiguration()?.maximizerUserUid ??
      this.payload()?.userId ??
      '';

    if (!configurationId || !maximizerUserUid) {
      this.store.reportError(
        this.translate.instant('outlookSync.messages.saveError'),
      );
      return;
    }

    const controls = this.form.controls;
    const existingContacts = this.userSyncConfiguration()?.contactsSync;
    const favoriteListId =
      controls.favoriteListIds.value?.[0] ??
      existingContacts?.favoriteListKey ??
      undefined;
    const favoriteListName =
      controls.favoriteListName.value ??
      existingContacts?.favoriteListName ??
      undefined;
    const outlookCategoryId =
      controls.outlookCategoryIds.value?.[0] ??
      existingContacts?.outlookCategory ??
      null;
    const syncEverything = !!controls.emailsSyncSyncEverything.value;
    const calendarDaysAfter = this.getCalendarRollingWindowDaysAfter(
      controls.appointmentsRollingWindowDaysAfter.value,
    );

    const settings: UpdateSyncConfiguration = {
      maximizerUserUid,
      contactsSync: {
        enabled: controls.contactsSyncEnabled.value,
        favoriteListKey: favoriteListId ?? undefined,
        favoriteListName,
        outlookCategory: outlookCategoryId ?? undefined,
      },
      appointmentsSync: {
        enabled: controls.appointmentsSyncEnabled.value,
        rollingWindowDaysBack: controls.appointmentsRollingWindowDaysBack.value,
        rollingWindowDaysAfter: calendarDaysAfter,
      },
      tasksSync: {
        enabled: controls.tasksSyncEnabled.value,
      },
      emailsSync: {
        enabled: controls.emailsSyncEnabled.value,
        syncEverything,
      },
    };

    const changed = this.computeSaveDelta();
    const hasChanges = Object.keys(changed).length > 0;

    this.store.beginSave();
    this.outlookSync
      .updateConfiguration(settings, configurationId)
      .pipe(
        catchError((error) => {
          console.error('Failed to save configuration', error);
          this.store.reportError(
            this.translate.instant('outlookSync.messages.saveError'),
          );
          if (hasChanges) {
            this.trackSyncConfigurationEvent('save', 'failed', {
              result: 'failed',
              errorMessage: normalizeError(error),
              changed,
            });
          }
          return of(false);
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }
        if (hasChanges) {
          this.trackSyncConfigurationEvent('save', 'completed', {
            result: 'success',
            contactsSyncEnabled: controls.contactsSyncEnabled.value,
            emailsSyncEnabled: controls.emailsSyncEnabled.value,
            appointmentsSyncEnabled: controls.appointmentsSyncEnabled.value,
            tasksSyncEnabled: controls.tasksSyncEnabled.value,
            emailMode: syncEverything ? 'auto' : 'manual',
            calendarDaysBack: controls.appointmentsRollingWindowDaysBack.value,
            calendarDaysAfter,
            changed,
          });
        }
        this.store.reportSuccess(
          this.translate.instant(
            'outlookSync.messages.updateSuccess',
          ),
        );
        this.form.markAsPristine();
        this.reloadConfiguration(configurationId);
        this.reenabledTypes.update((set) => {
          const next = new Set(set);
          for (const [key, type] of Object.entries(SYNC_DELTA_KEY_TO_TYPE)) {
            if (changed[key] === 'enabled') next.add(type);
            else if (changed[key] === 'disabled') next.delete(type);
          }
          return next;
        });
        this.fetchAllHistory()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  private computeSaveDelta(): Record<string, unknown> {
    const existing = this.userSyncConfiguration();
    const controls = this.form.controls;
    const changed: Record<string, unknown> = {};

    const toggle = (prev: boolean | undefined, next: boolean) => {
      if ((prev ?? false) === next) return undefined;
      return next ? 'enabled' : 'disabled';
    };

    const contactsToggle = toggle(
      existing?.contactsSync?.enabled,
      controls.contactsSyncEnabled.value,
    );
    if (contactsToggle) changed['contactsSync'] = contactsToggle;

    const emailsToggle = toggle(
      existing?.emailsSync?.enabled,
      controls.emailsSyncEnabled.value,
    );
    if (emailsToggle) changed['emailsSync'] = emailsToggle;

    const apptToggle = toggle(
      existing?.appointmentsSync?.enabled,
      controls.appointmentsSyncEnabled.value,
    );
    if (apptToggle) changed['appointmentsSync'] = apptToggle;

    const tasksToggle = toggle(
      existing?.tasksSync?.enabled,
      controls.tasksSyncEnabled.value,
    );
    if (tasksToggle) changed['tasksSync'] = tasksToggle;

    const prevEmailMode =
      existing?.emailsSync?.syncEverything === false ? 'manual' : 'auto';
    const nextEmailMode = controls.emailsSyncSyncEverything.value
      ? 'auto'
      : 'manual';
    if (prevEmailMode !== nextEmailMode) {
      changed['emailMode'] = nextEmailMode;
    }

    const prevDaysBack = existing?.appointmentsSync?.rollingWindowDaysBack;
    const nextDaysBack = controls.appointmentsRollingWindowDaysBack.value;
    if (prevDaysBack !== nextDaysBack) {
      changed['calendarDaysBack'] = nextDaysBack;
    }

    const prevDaysAfter = this.getCalendarRollingWindowDaysAfter(
      existing?.appointmentsSync?.rollingWindowDaysAfter,
    );
    const nextDaysAfter = this.getCalendarRollingWindowDaysAfter(
      controls.appointmentsRollingWindowDaysAfter.value,
    );
    if (prevDaysAfter !== nextDaysAfter) {
      changed['calendarDaysAfter'] = nextDaysAfter;
    }

    const prevFavoriteKey = existing?.contactsSync?.favoriteListKey ?? null;
    const nextFavoriteKey = controls.favoriteListIds.value?.[0] ?? null;
    if (prevFavoriteKey !== nextFavoriteKey) {
      changed['favoriteList'] = 'changed';
    }

    const prevCategory = existing?.contactsSync?.outlookCategory ?? null;
    const nextCategory = controls.outlookCategoryIds.value?.[0] ?? null;
    if (prevCategory !== nextCategory) {
      changed['outlookCategory'] = 'changed';
    }

    return changed;
  }

  protected async reauthenticate(): Promise<void> {
    if (this.reauthenticating()) {
      return;
    }
    const configurationId = this.getConfigurationId();
    const maximizerUserUid =
      this.userSyncConfiguration()?.maximizerUserUid ??
      this.payload()?.userId ??
      '';
    if (!configurationId || !maximizerUserUid) {
      this.store.reportError(
        this.translate.instant('outlookSync.messages.reauthError'),
      );
      return;
    }
    this.trackSyncConfigurationEvent('re-authenticate', 'started', {
      userSyncConfigurationId: configurationId,
    });
    this.reauthenticating.set(true);
    try {
      await this.oauthFlow.startMicrosoftReauth(
        configurationId,
        maximizerUserUid,
      );
    } finally {
      const error = this.oauthFlow.errorMessage();
      if (error) {
        this.reauthenticating.set(false);
        this.store.reportError(
          this.translate.instant('outlookSync.messages.reauthError'),
        );
        this.trackSyncConfigurationEvent('re-authenticate', 'failed', {
          userSyncConfigurationId: configurationId,
          errorMessage: error,
        });
      }
    }
  }

  protected close(): void {
    this.element.nativeElement.dispatchEvent(
      new CustomEvent('actionCompleted', {
        detail: { action: 'close' },
        bubbles: true,
      }),
    );

    globalThis.close();
  }

  private startHistoryPolling(): void {
    if (this.historyPollingStarted) {
      return;
    }
    this.historyPollingStarted = true;

    interval(HISTORY_POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.fetchAllHistory()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private fetchAllHistory(): Observable<unknown> {
    const configurationId = this.getConfigurationId();
    if (!configurationId) {
      return of(null);
    }
    return forkJoin(
      POLLED_SYNC_TYPES.map((type) =>
        this.outlookSync
          .getHistoryBySyncType(type, configurationId, HISTORY_PAGE_SIZE)
          .pipe(catchError(() => of(undefined))),
      ),
    );
  }

  protected get showConnectionWarning(): boolean {
    if (this.resumingReauth()) {
      return false;
    }
    return this.healthStatus()?.isMicrosoftAuthHealthy === false;
  }

  protected getSyncControl(tab: OutlookSyncTab): FormControl<boolean> {
    switch (tab) {
      case 'contacts':
        return this.form.controls.contactsSyncEnabled;
      case 'appointments':
        return this.form.controls.appointmentsSyncEnabled;
      case 'tasks':
        return this.form.controls.tasksSyncEnabled;
      case 'emails':
      default:
        return this.form.controls.emailsSyncEnabled;
    }
  }

  private applyPayload(payload: OutlookSyncConfigurationPayload): void {
    const normalizedPayload: OutlookSyncConfigurationPayload = {
      ...payload,
      emailsSyncSyncEverything: !!payload.emailsSyncSyncEverything,
      appointmentsRollingWindowDaysAfter:
        this.getCalendarRollingWindowDaysAfter(
          payload.appointmentsRollingWindowDaysAfter,
        ),
    };

    this.payload.set(normalizedPayload);

    this.form.patchValue(
      {
        contactsSyncEnabled: normalizedPayload.contactsSyncEnabled,
        appointmentsSyncEnabled: normalizedPayload.appointmentsSyncEnabled,
        tasksSyncEnabled: normalizedPayload.tasksSyncEnabled,
        emailsSyncEnabled: normalizedPayload.emailsSyncEnabled,
        emailsSyncSyncEverything: normalizedPayload.emailsSyncSyncEverything,
        appointmentsRollingWindowDaysBack:
          normalizedPayload.appointmentsRollingWindowDaysBack ?? 180,
        appointmentsRollingWindowDaysAfter:
          normalizedPayload.appointmentsRollingWindowDaysAfter ?? 30,
        favoriteListIds: normalizedPayload.favoriteListOptions
          ? normalizedPayload.favoriteListOptions.map((option) => option.id)
          : [],
        outlookCategoryIds: normalizedPayload.outlookCategoryOptions
          ? normalizedPayload.outlookCategoryOptions.map((option) => option.id)
          : [],
      },
      { emitEvent: false },
    );
  }

  private selectTab(tab: OutlookSyncTab): void {
    this.store.setSelectedTab(tab);
    if (tab === this.activeTab()) {
      return;
    }
    this.activeTab.set(tab);
  }

  private trackSyncConfigurationEvent(
    journey: string,
    action: string,
    metadata: Record<string, unknown>,
  ): void {
    this.trackEvent.trackEvent({
      module: TRACKER_MODULE,
      journey,
      action,
      metadata,
    });
  }

  private getConfigurationId(): string | null {
    return (
      this.userSyncConfiguration()?.id ??
      this.payload()?.userSyncConfigurationId ??
      null
    );
  }

  private reloadConfiguration(configurationId: string): void {
    this.outlookSync.getConfigurationById(configurationId).subscribe({
      next: (configuration) => {
        if (!configuration) {
          this.store.reportConfigLoadFailure();
          return;
        }
        this.store.reportConfigLoadSuccess();
        this.userSyncConfiguration.set(configuration);
        this.updatePayloadFromConfiguration(configuration);
      },
      error: (error) => {
        console.error('Unable to reload Outlook sync configuration', error);
        if (
          error instanceof HttpErrorResponse &&
          error.status === HttpStatusCode.Unauthorized
        ) {
          this.store.reportAuthFailure();
        } else {
          this.store.reportConfigLoadFailure();
        }
      },
    });
  }

  private updatePayloadFromConfiguration(
    configuration: UserSyncConfiguration,
  ): void {
    const current = this.payload();
    if (!current) {
      return;
    }

    const updatedPayload: OutlookSyncConfigurationPayload = {
      ...current,
      contactsSyncEnabled:
        configuration.contactsSync?.enabled ?? current.contactsSyncEnabled,
      appointmentsSyncEnabled:
        configuration.appointmentsSync?.enabled ??
        current.appointmentsSyncEnabled,
      appointmentsRollingWindowDaysBack:
        configuration.appointmentsSync?.rollingWindowDaysBack ??
        current.appointmentsRollingWindowDaysBack,
      appointmentsRollingWindowDaysAfter:
        this.getCalendarRollingWindowDaysAfter(
          configuration.appointmentsSync?.rollingWindowDaysAfter ??
            current.appointmentsRollingWindowDaysAfter,
        ),
      tasksSyncEnabled:
        configuration.tasksSync?.enabled ?? current.tasksSyncEnabled,
      emailsSyncEnabled:
        configuration.emailsSync?.enabled ?? current.emailsSyncEnabled,
      emailsSyncSyncEverything: !!configuration.emailsSync?.syncEverything,
      lockedForSync: configuration.lockedForSync ?? current.lockedForSync,
      needsManualIntervention:
        configuration.needsManualIntervention ??
        current.needsManualIntervention,
    };

    this.payload.set(updatedPayload);

    this.form.patchValue(
      {
        contactsSyncEnabled: updatedPayload.contactsSyncEnabled,
        appointmentsSyncEnabled: updatedPayload.appointmentsSyncEnabled,
        appointmentsRollingWindowDaysBack:
          updatedPayload.appointmentsRollingWindowDaysBack ?? 180,
        appointmentsRollingWindowDaysAfter:
          updatedPayload.appointmentsRollingWindowDaysAfter,
        tasksSyncEnabled: updatedPayload.tasksSyncEnabled,
        emailsSyncEnabled: updatedPayload.emailsSyncEnabled,
        emailsSyncSyncEverything:
          updatedPayload.emailsSyncSyncEverything ?? true,
      },
      { emitEvent: false },
    );

    this.setSyncStatus(updatedPayload);
  }

  private getCalendarRollingWindowDaysAfter(
    value: number | null | undefined,
  ): number {
    const defaultDaysAfter = OUTLOOK_SYNC_FREQUENCY.calendar.defaultDaysAfter;
    return this.isCalendarRollingWindowDaysAfterShown()
      ? (value ?? defaultDaysAfter)
      : defaultDaysAfter;
  }

  private isCalendarRollingWindowDaysAfterShown(): boolean {
    return this.feature.isFeatureOn(
      OUTLOOK_SYNC_CALENDAR_ROLLING_WINDOW_FEATURE_FLAG,
      false,
    );
  }

  private registerTabStatusListeners(): void {
    const controls = this.form.controls;

    merge(
      controls.emailsSyncEnabled.valueChanges,
      controls.contactsSyncEnabled.valueChanges,
      controls.appointmentsSyncEnabled.valueChanges,
      controls.tasksSyncEnabled.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setSyncStatusFromForm());

    this.setSyncStatusFromForm();
  }

  private setSyncStatusFromForm(): void {
    const controls = this.form.controls;
    this.applyTabStatuses({
      emails: controls.emailsSyncEnabled.value,
      contacts: controls.contactsSyncEnabled.value,
      appointments: controls.appointmentsSyncEnabled.value,
      tasks: controls.tasksSyncEnabled.value,
    });
  }

  private setSyncStatus(value: OutlookSyncConfigurationPayload): void {
    this.applyTabStatuses({
      emails: value.emailsSyncEnabled,
      contacts: value.contactsSyncEnabled,
      appointments: value.appointmentsSyncEnabled,
      tasks: value.tasksSyncEnabled,
    });
  }

  private applyTabStatuses(statuses: Record<OutlookSyncTab, boolean>): void {
    for (const tab of this.tabs) {
      tab.status = statuses[tab.tab] ? 'active' : 'paused';
    }
  }
}
